import { useQuery } from '@tanstack/react-query'
import { authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { ApiError, type ProblemDetail } from '@/lib/errors'

/**
 * Hand-written types for ``GET /api/v1/analytics/costs``. The endpoint was
 * added in this branch, so ``types/api.ts`` (generated by
 * ``scripts/gen_ts_client.sh`` from /openapi.json) doesn't carry it yet.
 * When the script is re-run, swap these for ``components['schemas'][...]``.
 */
export interface CostTimeseriesPoint {
  date: string
  total_usd: number
  calls: number
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  cache_write_tokens: number
  web_searches: number
}

export interface CostFeatureBreakdownItem {
  feature: string
  total_usd: number
  calls: number
}

export interface CostReport {
  days: number
  channel_slug: string | null
  factory: string | null
  total_usd: number
  timeseries: CostTimeseriesPoint[]
  by_feature: CostFeatureBreakdownItem[]
}

interface Envelope<T> {
  data: T
}

export interface UseCostReportParams {
  /** Lookback window in days (1..365). Defaults to 30. */
  days?: number
  /** Optional channel filter. ``null`` = all channels. */
  channel?: string | null
  /** Optional factory/feature filter. ``null`` = all subsystems. */
  factory?: string | null
}

/**
 * Daily LLM cost for the Reports page. Backed by ``llm_api_calls``
 * (every call that goes through ``estimate_cost(feature=...)`` is logged
 * with its USD cost — this aggregates them).
 *
 * ``staleTime`` is generous (5 min) — costs change slowly; rapid refetch
 * is wasteful and the data is decision-grade, not real-time.
 */
export function useCostReport({ days = 30, channel = null, factory = null }: UseCostReportParams = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.costs({ days, channel, factory }),
    queryFn: async (): Promise<CostReport> => {
      const params = new URLSearchParams()
      params.set('days', String(days))
      if (channel) params.set('channel_slug', channel)
      if (factory) params.set('factory', factory)
      const res = await authFetch(`/api/v1/analytics/costs?${params.toString()}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message, res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<CostReport>
      return json.data
    },
    staleTime: 5 * 60_000,
  })
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (!body || typeof body !== 'object') return false
  const obj = body as Record<string, unknown>
  return typeof obj['title'] === 'string' && typeof obj['status'] === 'number'
}
